import { useState } from "react";
import { formatCurrency } from "../../utils/format";

const PRESET_AMOUNTS = [1000, 2500, 5000, 10000]; // Pence

interface TopUpFormProps {
  onSubmit: (amountPence: number) => Promise<void>;
  loading?: boolean;
}

export default function TopUpForm({ onSubmit, loading = false }: TopUpFormProps) {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [error, setError] = useState<string | null>(null);

  const effectiveAmount = selectedPreset ?? (customAmount ? Math.round(parseFloat(customAmount) * 100) : 0);

  function handlePresetClick(amount: number) {
    setSelectedPreset(amount);
    setCustomAmount("");
    setError(null);
  }

  function handleCustomChange(value: string) {
    setSelectedPreset(null);
    setCustomAmount(value);
    setError(null);
  }

  async function handleSubmit() {
    if (effectiveAmount < 1000) {
      setError("Minimum top-up amount is £10.00");
      return;
    }
    if (!Number.isFinite(effectiveAmount) || effectiveAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    try {
      await onSubmit(effectiveAmount);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment failed");
    }
  }

  return (
    <div className="space-y-6">
      {/* Preset amounts */}
      <div>
        <label className="mb-3 block text-sm font-medium text-gray-700">
          Select an amount
        </label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {PRESET_AMOUNTS.map((amount) => (
            <button
              key={amount}
              onClick={() => handlePresetClick(amount)}
              disabled={loading}
              className={`rounded-lg border-2 px-4 py-4 text-center text-lg font-semibold transition-colors ${
                selectedPreset === amount
                  ? "border-simsy-600 bg-simsy-50 text-simsy-700"
                  : "border-gray-200 bg-white text-gray-900 hover:border-simsy-300 hover:bg-simsy-50"
              }`}
            >
              {formatCurrency(amount)}
            </button>
          ))}
        </div>
      </div>

      {/* Custom amount */}
      <div>
        <label
          htmlFor="custom-amount"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Or enter a custom amount (min £10.00)
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500">£</span>
          </div>
          <input
            id="custom-amount"
            type="number"
            min="10"
            step="0.01"
            placeholder="0.00"
            value={customAmount}
            onChange={(e) => handleCustomChange(e.target.value)}
            disabled={loading}
            className="block w-full rounded-lg border border-gray-300 py-3 pl-8 pr-4 text-lg focus:border-simsy-500 focus:outline-none focus:ring-2 focus:ring-simsy-500"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Summary + submit */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Top-up amount</span>
          <span className="text-lg font-semibold text-gray-900">
            {effectiveAmount >= 1000
              ? formatCurrency(effectiveAmount)
              : "—"}
          </span>
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={loading || effectiveAmount < 1000}
        className="w-full rounded-lg bg-simsy-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-simsy-700 focus:outline-none focus:ring-2 focus:ring-simsy-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? "Processing..." : "Pay with Card"}
      </button>
    </div>
  );
}
